import { useState, useRef, useEffect, useCallback } from '@wordpress/element';

/* ════════════════════════════════════════════════
   TextInput  — replaces TextControl
   ════════════════════════════════════════════════ */
export const TextInput = ({ value = '', onChange, type = 'text', placeholder = '' }) => (
    <input
        className="cpiw-input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
    />
);

/* ════════════════════════════════════════════════
   Toggle  — replaces CheckboxControl
   Shows a labelled toggle switch (enable / disable)
   ════════════════════════════════════════════════ */
export const Toggle = ({ checked, onChange, label }) => (
    <label className="cpiw-toggle">
        <span
            className={`cpiw-toggle__track${checked ? ' cpiw-toggle__track--on' : ''}`}
            role="switch"
            aria-checked={checked}
            tabIndex={0}
            onClick={() => onChange(!checked)}
            onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && onChange(!checked)}
        >
            <span className="cpiw-toggle__thumb" />
        </span>
        {label && (
            <span className={`cpiw-toggle__label${checked ? ' cpiw-toggle__label--on' : ''}`}>
                {checked ? 'Enabled' : 'Disabled'}
            </span>
        )}
    </label>
);

/* ════════════════════════════════════════════════
   Select  — replaces SelectControl / CustomSelectControl
   ════════════════════════════════════════════════ */
export const Select = ({ value, options = [], onChange }) => (
    <select
        className="cpiw-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
    >
        {options.map((opt) => (
            <option
                key={opt.value ?? opt.key}
                value={opt.value ?? opt.key}
                disabled={!!opt.disabled}
            >
                {opt.label ?? opt.name}
            </option>
        ))}
    </select>
);

/* ════════════════════════════════════════════════
   RadioGroup  — replaces RadioControl
   ════════════════════════════════════════════════ */
export const RadioGroup = ({ selected, options = [], onChange }) => (
    <div className="cpiw-radio-group">
        {options.map((opt) => {
            const val = opt.value ?? opt.key;
            const lbl = opt.label ?? opt.name;
            return (
                <label key={val} className={`cpiw-radio${selected === val ? ' cpiw-radio--active' : ''}`}>
                    <input
                        type="radio"
                        name={`radio-${val}`}
                        value={val}
                        checked={selected === val}
                        onChange={() => onChange(val)}
                    />
                    {typeof lbl === 'string' ? lbl : <span dangerouslySetInnerHTML={{ __html: lbl }} />}
                </label>
            );
        })}
    </div>
);

/* ════════════════════════════════════════════════
   ColorPicker  — replaces ColorPalette
   Native <input type="color"> + hex text input
   ════════════════════════════════════════════════ */
export const ColorPicker = ({ value = '#000000', onChange }) => {
    const [hex, setHex]       = useState(value || '#000000');
    const [open, setOpen]     = useState(false);
    const wrapRef             = useRef(null);

    // Sync if parent value changes
    useEffect(() => { setHex(value || '#000000'); }, [value]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleHexInput = (e) => {
        const v = e.target.value;
        setHex(v);
        if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange(v);
    };

    const handleNative = (e) => {
        const v = e.target.value;
        setHex(v);
        onChange(v);
    };

    // Preset swatches — common WooCommerce admin colours
    const presets = [
        '#000000','#ffffff','#ef4444','#f97316','#eab308',
        '#22c55e','#3b82f6','#8b5cf6','#ec4899','#6366f1',
        '#10b981','#14b8a6','#f59e0b','#64748b','#94a3b8',
    ];

    return (
        <div className="cpiw-color-picker" ref={wrapRef}>
            {/* Trigger button */}
            <button
                type="button"
                className="cpiw-color-trigger"
                onClick={() => setOpen(o => !o)}
                aria-label="Pick colour"
            >
                <span className="cpiw-color-trigger__swatch" style={{ background: hex }} />
                <span className="cpiw-color-trigger__hex">{hex}</span>
                <span className="cpiw-color-trigger__arrow">{open ? '▲' : '▼'}</span>
            </button>

            {/* Dropdown */}
            {open && (
                <div className="cpiw-color-dropdown">
                    {/* Native colour wheel */}
                    <div className="cpiw-color-wheel-wrap">
                        <input
                            type="color"
                            className="cpiw-color-wheel"
                            value={hex}
                            onChange={handleNative}
                        />
                        <span className="cpiw-color-wheel-hint">Click to open wheel</span>
                    </div>

                    {/* Presets */}
                    <div className="cpiw-color-presets">
                        {presets.map(c => (
                            <button
                                key={c}
                                type="button"
                                className={`cpiw-color-preset${hex === c ? ' cpiw-color-preset--active' : ''}`}
                                style={{ background: c }}
                                onClick={() => { setHex(c); onChange(c); }}
                                title={c}
                            />
                        ))}
                    </div>

                    {/* Hex text */}
                    <div className="cpiw-color-hex-row">
                        <span className="cpiw-color-hex-label">HEX</span>
                        <input
                            className="cpiw-color-hex-input"
                            type="text"
                            value={hex}
                            maxLength={7}
                            onChange={handleHexInput}
                            spellCheck={false}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

/* ════════════════════════════════════════════════
   FileUpload  — replaces FormFileUpload
   ════════════════════════════════════════════════ */
export const FileUpload = ({ accept, onChange, children }) => {
    const inputRef = useRef(null);
    return (
        <>
            <button
                type="button"
                className="cpiw-file-btn"
                onClick={() => inputRef.current?.click()}
            >
                {children}
            </button>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={onChange}
            />
        </>
    );
};
